import React, { useState, useEffect } from 'react';
import { Header } from '@/components/Header';
import BottomNav from '@/components/BottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';
import { Sparkles, Copy, Check, RotateCcw, Crown, ChevronDown, ChevronRight, ArrowRight, Bookmark, BookmarkCheck } from 'lucide-react';
import { Seo } from '@/components/Seo';
import { ErrorDino } from '@/components/DetectiveDino';

const TONES = [
  { value: 'casual', tKey: 'toneCasual' },
  { value: 'direct', tKey: 'toneDirect' },
  { value: 'empathetic', tKey: 'toneEmpathetic' },
  { value: 'formal', tKey: 'toneFormal' },
  { value: 'friendly', tKey: 'toneFriendly' },
  { value: 'humorous', tKey: 'toneHumorous' },
  { value: 'passive', tKey: 'tonePassive' },
  { value: 'persuasive', tKey: 'tonePersuasive' },
  { value: 'polite', tKey: 'tonePolite' },
  { value: 'professional', tKey: 'toneProfessional' },
];

const INPUT_LANGUAGES = [
  {
    group: 'english', tKey: 'langEnglish',
    subs: [
      { value: 'en-northamerica', tKey: 'langEnNorthAmerica' },
      { value: 'en-african', tKey: 'langEnAfrican' },
      { value: 'en-european', tKey: 'langEnEuropean' },
      { value: 'en-asian', tKey: 'langEnAsian' },
      { value: 'en-australian', tKey: 'langEnAustralian' },
    ]
  },
  {
    group: 'spanish', tKey: 'langSpanish',
    subs: [
      { value: 'es-northamerica', tKey: 'langEsNorthAmerica' },
      { value: 'es-southamerica', tKey: 'langEsSouthAmerica' },
      { value: 'es-european', tKey: 'langEsEuropean' },
      { value: 'es-caribbean', tKey: 'langEsCaribbean' },
      { value: 'es-asian', tKey: 'langEsAsian' },
    ]
  },
  { group: 'hindi', tKey: 'langHindi', value: 'hindi', subs: [] },
  { group: 'korean', tKey: 'langKorean', value: 'korean', subs: [] },
  { group: 'japanese', tKey: 'langJapanese', value: 'japanese', subs: [] },
  { group: 'german', tKey: 'langGerman', value: 'german', subs: [] },
];

const OUTPUT_LANGUAGES = [
  {
    group: 'english', tKey: 'langEnglish',
    subs: [
      { value: 'en-northamerica', tKey: 'langEnNorthAmerica' },
      { value: 'en-african', tKey: 'langEnAfrican' },
      { value: 'en-european', tKey: 'langEnEuropean' },
      { value: 'en-asian', tKey: 'langEnAsian' },
      { value: 'en-australian', tKey: 'langEnAustralian' },
    ]
  },
  {
    group: 'spanish', tKey: 'langSpanish',
    subs: [
      { value: 'es-northamerica', tKey: 'langEsNorthAmerica' },
      { value: 'es-southamerica', tKey: 'langEsSouthAmerica' },
      { value: 'es-european', tKey: 'langEsEuropean' },
      { value: 'es-caribbean', tKey: 'langEsCaribbean' },
      { value: 'es-asian', tKey: 'langEsAsian' },
    ]
  },
];

// Get display label for a language value using t()
const getLangLabel = (value, t) => {
  for (const group of [...INPUT_LANGUAGES, ...OUTPUT_LANGUAGES]) {
    if (group.value === value) return t(group.tKey);
    for (const sub of group.subs) {
      if (sub.value === value) return t(sub.tKey);
    }
  }
  return value;
};

// Language picker with expandable groups
const LanguagePicker = ({ languages, value, onChange, label, testId, t }) => {
  const [expandedGroup, setExpandedGroup] = useState(null);

  useEffect(() => {
    for (const group of languages) {
      if (group.subs.some(s => s.value === value)) {
        setExpandedGroup(group.group);
        break;
      }
    }
  }, [value, languages]);

  return (
    <div>
      <Label className="text-sm font-medium">{label}</Label>
      <div className="mt-1 border rounded-md divide-y bg-background max-h-48 overflow-y-auto" data-testid={testId}>
        {languages.map(group => {
          const hasSubs = group.subs.length > 0;
          const isExpanded = expandedGroup === group.group;
          const isSelected = group.value === value || group.subs.some(s => s.value === value);

          return (
            <div key={group.group}>
              <button
                type="button"
                className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-muted/50 transition-colors ${
                  !hasSubs && isSelected ? 'bg-[#00E5CC]/10 text-[#00E5CC] font-medium' : ''
                }`}
                onClick={() => {
                  if (hasSubs) {
                    setExpandedGroup(isExpanded ? null : group.group);
                  } else {
                    onChange(group.value);
                  }
                }}
              >
                <span>{t(group.tKey)}</span>
                {hasSubs ? (
                  isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                ) : (
                  isSelected && <Check className="h-4 w-4 text-[#00E5CC]" />
                )}
              </button>
              {hasSubs && isExpanded && (
                <div className="bg-muted/30">
                  {group.subs.map(sub => (
                    <button
                      key={sub.value}
                      type="button"
                      className={`w-full flex items-center justify-between px-3 pl-7 py-1.5 text-sm hover:bg-muted/50 transition-colors ${
                        sub.value === value ? 'bg-[#00E5CC]/10 text-[#00E5CC] font-medium' : 'text-muted-foreground'
                      }`}
                      onClick={() => onChange(sub.value)}
                      data-testid={`lang-${sub.value}`}
                    >
                      <span>{t(sub.tKey)}</span>
                      {sub.value === value && <Check className="h-3 w-3 text-[#00E5CC]" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ThoughtInterpreterPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [inputText, setInputText] = useState('');
  const [tone, setTone] = useState('polite');
  const [inputLang, setInputLang] = useState('en-northamerica');
  const [outputLang, setOutputLang] = useState('en-northamerica');
  const [result, setResult] = useState(null);
  const [decodeResult, setDecodeResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [usage, setUsage] = useState(null);
  const [history, setHistory] = useState([]);
  const [showInputLangPicker, setShowInputLangPicker] = useState(false);
  const [showOutputLangPicker, setShowOutputLangPicker] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  // "rephrase" = neurodivergent → neurotypical (original mode)
  // "decode"   = neurotypical → neurodivergent-friendly (new May 2026)
  const [mode, setMode] = useState('rephrase');

  const isVip = user?.subscription_status === 'active';

  useEffect(() => {
    (async () => {
      const { data } = await apiFetch('/api/translate/usage');
      if (data) setUsage(data);
    })();
  }, [result]);

  const handleRephrase = async () => {
    if (!inputText.trim()) {
      toast.error(t('pleaseTypeSomething'));
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Use direct fetch to avoid potential Service Worker body-read conflicts
      const token = localStorage.getItem('auth_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch('/api/translate/rephrase', {
        method: 'POST',
        headers,
        body: JSON.stringify({ text: inputText.trim(), tone, input_language: inputLang, output_language: outputLang }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        const errMsg = data?.detail || `Request failed (${response.status})`;
        if (errMsg.includes('Daily limit')) {
          toast.error(t('dailyLimitVip'));
        } else {
          toast.error(errMsg);
        }
        return;
      }

      setResult(data);
      setSaved(false);
      setCelebrate(true);
      setTimeout(() => setCelebrate(false), 700);
      setHistory(prev => [{ ...data, timestamp: new Date().toISOString() }, ...prev].slice(0, 10));
      if (data.remaining_today !== null && data.remaining_today !== undefined) {
        setUsage(prev => prev ? { ...prev, remaining: data.remaining_today } : prev);
      }
      try {
        const { track } = await import('@/lib/analytics');
        track.interpreterRephrase(tone, `${inputLang}->${outputLang}`);
      } catch (analyticsErr) {
        // Analytics failure must never break the user flow. Surface
        // in dev tools for debugging, but don't bubble up.
        console.debug('[analytics] interpreterRephrase failed:', analyticsErr);
      }
    } catch (e) {
      toast.error(t('failedToInterpret'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (result?.rephrased) {
      await navigator.clipboard.writeText(result.rephrased);
      setCopied(true);
      toast.success(t('copied'));
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleReset = () => {
    setInputText('');
    setResult(null);
    setDecodeResult(null);
    setSaved(false);
  };

  // Decode neurotypical message → literal meaning + intent + suggested reply.
  // Mirrors handleRephrase but hits /api/translate/decode and stores the
  // structured response in `decodeResult`.
  const handleDecode = async () => {
    if (!inputText.trim()) {
      toast.error(t('pleaseTypeSomething'));
      return;
    }
    setLoading(true);
    setDecodeResult(null);
    setResult(null);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch('/api/translate/decode', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          text: inputText.trim(),
          input_language: inputLang,
          output_language: outputLang,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        const errMsg = data?.detail || `Request failed (${response.status})`;
        if (errMsg.includes('Daily limit')) {
          toast.error(t('dailyLimitVip'));
        } else {
          toast.error(errMsg);
        }
        return;
      }
      setDecodeResult(data);
      setCelebrate(true);
      setTimeout(() => setCelebrate(false), 1200);
    } catch (e) {
      toast.error(`Decode failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const copyFieldText = async (key, text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(key);
      toast.success(t('copied'));
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error('Clipboard blocked.');
    }
  };

  const handleSave = async () => {
    if (!result?.rephrased) return;
    if (!user) {
      toast.error(t('signInToSave'));
      return;
    }
    setSaving(true);
    try {
      const { data, error } = await apiFetch('/api/translate/favorites', {
        method: 'POST',
        body: {
          original: result.original,
          rephrased: result.rephrased,
          tone: result.tone,
          input_language: result.input_language,
          output_language: result.output_language,
        },
      });
      if (error) {
        toast.error(t('failedToSave'));
        return;
      }
      setSaved(true);
      toast.success(data?.duplicate ? t('alreadySaved') : t('savedToFavorites'));
      if (!data?.duplicate) {
        try {
          const { track } = await import('@/lib/analytics');
          track.rephraseSaved();
        } catch (analyticsErr) {
          console.debug('[analytics] rephraseSaved failed:', analyticsErr);
        }
      }
    } catch (e) {
      toast.error(t('failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <Seo
        path="/interpreter"
        title="Thought Interpreter — ClearCommunicationApp"
        description="Translate raw thoughts into clear, neurotypical-friendly language. AI-powered communication translator for direct, masked, gentle, or formal styles."
      />
      <Header />
      <div className="container py-4 sm:py-6">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Header */}
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center gap-3">
              <ErrorDino size={72} className="shrink-0" animated={loading} />
              <h1 className="text-2xl sm:text-3xl font-bold" data-testid="interpreter-title">
                {mode === 'decode' ? (t('decoderTitle') || 'Neurotypical Decoder') : t('thoughtInterpreter')}
              </h1>
            </div>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {mode === 'decode'
                ? (t('decoderSubtitle') || "Paste what they said. Get what they actually meant + a reply you can mirror back.")
                : t('interpreterSubtitle')}
            </p>
          </div>

          {/* Mode toggle — pill-style segmented control.
              "Rephrase my thought" (default) is the existing
              neurodivergent → neurotypical flow.
              "Decode their words" (new) hits /api/translate/decode
              and shows literal meaning + intent + suggested reply. */}
          <div
            className="mx-auto flex w-full max-w-md items-center rounded-full bg-muted p-1"
            data-testid="interpreter-mode-toggle"
            role="tablist"
          >
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'rephrase'}
              onClick={() => { setMode('rephrase'); setResult(null); setDecodeResult(null); }}
              className={`flex-1 rounded-full py-1.5 px-3 text-xs sm:text-sm font-medium transition-colors ${
                mode === 'rephrase'
                  ? 'bg-[#00E5CC] text-[#0d1f2d] shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="mode-rephrase-btn"
            >
              {t('modeRephrase') || 'Rephrase my thought'}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'decode'}
              onClick={() => { setMode('decode'); setResult(null); setDecodeResult(null); }}
              className={`flex-1 rounded-full py-1.5 px-3 text-xs sm:text-sm font-medium transition-colors ${
                mode === 'decode'
                  ? 'bg-[#8B5CF6] text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="mode-decode-btn"
            >
              {t('modeDecode') || 'Decode their words'}
            </button>
          </div>

          {/* Usage indicator — only for authenticated free-tier users.
              Guests are unlimited (no 5/day cap), and VIPs already
              short-circuit via `usage.is_vip`. */}
          {usage && user && !usage.is_vip && (
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground" data-testid="usage-indicator">
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full ${i < (usage.remaining || 0) ? 'bg-[#00E5CC]' : 'bg-muted'}`}
                  />
                ))}
              </div>
              <span>
                {usage.remaining}/{usage.limit} {t('remainingToday')}
              </span>
              {usage.remaining === 0 && (
                <a href="/subscription" className="text-[#00E5CC] font-medium hover:underline flex items-center gap-1">
                  <Crown className="h-3 w-3" /> VIP
                </a>
              )}
            </div>
          )}

          {/* Input Card */}
          <Card data-testid="interpreter-input-card">
            <CardContent className="pt-5 space-y-4">
              {/* Language selectors — two separate toggles */}
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="flex-1 flex items-center justify-between text-sm py-2 px-3 rounded-md border bg-muted/30 hover:bg-muted/50 transition-colors"
                  onClick={() => { setShowInputLangPicker(!showInputLangPicker); setShowOutputLangPicker(false); }}
                  data-testid="input-lang-toggle"
                >
                  <span className="font-medium text-foreground text-xs">{getLangLabel(inputLang, t)}</span>
                  {showInputLangPicker ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                </button>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <button
                  type="button"
                  className="flex-1 flex items-center justify-between text-sm py-2 px-3 rounded-md border bg-muted/30 hover:bg-muted/50 transition-colors"
                  onClick={() => { setShowOutputLangPicker(!showOutputLangPicker); setShowInputLangPicker(false); }}
                  data-testid="output-lang-toggle"
                >
                  <span className="font-medium text-foreground text-xs">{getLangLabel(outputLang, t)}</span>
                  {showOutputLangPicker ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                </button>
              </div>

              {/* Input language picker */}
              {showInputLangPicker && (
                <LanguagePicker
                  languages={INPUT_LANGUAGES}
                  value={inputLang}
                  onChange={(v) => { setInputLang(v); setShowInputLangPicker(false); }}
                  label={t('inputLanguage')}
                  testId="input-lang-picker"
                  t={t}
                />
              )}

              {/* Output language picker */}
              {showOutputLangPicker && (
                <LanguagePicker
                  languages={OUTPUT_LANGUAGES}
                  value={outputLang}
                  onChange={(v) => { setOutputLang(v); setShowOutputLangPicker(false); }}
                  label={t('outputLanguage')}
                  testId="output-lang-picker"
                  t={t}
                />
              )}

              {/* Text input */}
              <div>
                <Label className="text-sm font-medium">
                  {mode === 'decode'
                    ? (t('decoderInputLabel') || "Paste their message")
                    : t('whatDoYouWantToSay')}
                </Label>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder={mode === 'decode'
                    ? (t('decoderPlaceholder') || "Let's circle back on this — would be great if you could revisit the deliverable.")
                    : t('interpreterPlaceholder')}
                  className="mt-1 w-full min-h-[100px] p-3 rounded-md border bg-background text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#00E5CC] focus:border-transparent"
                  maxLength={2000}
                  data-testid="interpreter-input"
                />
                <p className="text-xs text-muted-foreground text-right mt-1">{inputText.length}/2000</p>
              </div>

              {/* Tone selector — only shown in rephrase mode. Decode mode
                  output is always literal + emotionally explicit. */}
              {mode === 'rephrase' && (
                <div>
                  <Label className="text-sm font-medium">{t('tone')}</Label>
                  <Select value={tone} onValueChange={setTone}>
                    <SelectTrigger className="mt-1" data-testid="tone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TONES.map(tn => (
                        <SelectItem key={tn.value} value={tn.value}>
                          {t(tn.tKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={mode === 'decode' ? handleDecode : handleRephrase}
                  disabled={loading || !inputText.trim()}
                  className={`flex-1 font-medium ${
                    mode === 'decode'
                      ? 'bg-[#8B5CF6] hover:bg-[#7C4DEF] text-white'
                      : 'bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d]'
                  }`}
                  data-testid={mode === 'decode' ? 'decode-btn' : 'interpret-btn'}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {mode === 'decode' ? (t('decoding') || 'Decoding…') : t('interpreting')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      {mode === 'decode' ? (t('decode') || 'Decode') : t('interpret')}
                    </span>
                  )}
                </Button>
                {(inputText || result || decodeResult) && (
                  <Button variant="outline" onClick={handleReset} data-testid="reset-btn">
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Result Card */}
          {result && (
            <Card className="border-[#00E5CC]/50 bg-[#00E5CC]/5" data-testid="interpreter-result-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ErrorDino size={32} className="shrink-0" animated={celebrate} />
                  {t('interpretedVersion')}
                  <span className="text-xs font-normal text-muted-foreground capitalize ml-auto">
                    {TONES.find(tn => tn.value === result.tone) ? t(TONES.find(tn => tn.value === result.tone).tKey) : result.tone}
                  </span>
                </CardTitle>
                {result.input_language !== result.output_language && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    {getLangLabel(result.input_language, t)}
                    <ArrowRight className="h-3 w-3" />
                    {getLangLabel(result.output_language, t)}
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="p-3 rounded-lg bg-background border text-foreground text-sm leading-relaxed" data-testid="rephrased-text">
                  {result.rephrased}
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopy}
                    className="flex-1"
                    data-testid="copy-btn"
                  >
                    {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                    {copied ? t('copied') : t('copy')}
                  </Button>
                  {user && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSave}
                      disabled={saving || saved}
                      className={`flex-1 ${saved ? 'bg-[#00E5CC]/10 text-[#00E5CC] border-[#00E5CC]/40' : ''}`}
                      data-testid="save-btn"
                    >
                      {saved ? <BookmarkCheck className="h-4 w-4 mr-1" /> : <Bookmark className="h-4 w-4 mr-1" />}
                      {saved ? t('saved') : t('save')}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRephrase}
                    disabled={loading}
                    className="flex-1"
                    data-testid="retry-btn"
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    {t('tryAgain')}
                  </Button>
                </div>

                {result.tips?.length > 0 && (
                  <p className="text-xs text-muted-foreground italic">{result.tips[0]}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Decode result — structured: literal meaning + intent +
              suggested response + flags. Distinct purple/violet accent
              vs the teal rephrase card so the user always knows which
              direction they're translating. */}
          {decodeResult && (
            <Card
              className="border-[#8B5CF6]/50 bg-[#8B5CF6]/5"
              data-testid="decoder-result-card"
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ErrorDino size={32} className="shrink-0" animated={celebrate} />
                  {t('decoderHeading') || 'What they actually mean'}
                  {decodeResult.flags?.length > 0 && (
                    <span className="ml-auto flex flex-wrap gap-1 justify-end">
                      {decodeResult.flags.map((f) => (
                        <span
                          key={f}
                          className="text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#8B5CF6]/15 text-[#7C4DEF] dark:text-[#C4A8FF]"
                          data-testid={`decode-flag-${f}`}
                        >
                          {f.replace(/-/g, ' ')}
                        </span>
                      ))}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Literal meaning — most important, biggest emphasis */}
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8B5CF6] mb-1">
                    {t('decoderLiteralLabel') || 'Literal meaning'}
                  </p>
                  <div className="flex items-start gap-2">
                    <p
                      className="flex-1 p-3 rounded-lg bg-background border text-foreground text-sm leading-relaxed font-medium"
                      data-testid="decode-literal"
                    >
                      {decodeResult.literal_meaning}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 shrink-0"
                      onClick={() => copyFieldText('literal', decodeResult.literal_meaning)}
                      title="Copy literal meaning"
                      data-testid="decode-copy-literal"
                    >
                      {copiedField === 'literal' ? <Check className="h-3.5 w-3.5 text-[#00E5CC]" /> : <Copy className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </div>

                {/* Likely intent — the social subtext */}
                {decodeResult.likely_intent && (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                      {t('decoderIntentLabel') || 'Why they said it that way'}
                    </p>
                    <p
                      className="p-3 rounded-lg bg-background/60 border border-dashed text-foreground/90 text-sm leading-relaxed"
                      data-testid="decode-intent"
                    >
                      {decodeResult.likely_intent}
                    </p>
                  </div>
                )}

                {/* Suggested response — copy-paste ready */}
                {decodeResult.suggested_response && (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#00A896] mb-1">
                      {t('decoderResponseLabel') || 'Reply you can use'}
                    </p>
                    <div className="flex items-start gap-2">
                      <p
                        className="flex-1 p-3 rounded-lg bg-[#00E5CC]/5 border border-[#00E5CC]/30 text-foreground text-sm leading-relaxed"
                        data-testid="decode-suggested"
                      >
                        {decodeResult.suggested_response}
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 shrink-0"
                        onClick={() => copyFieldText('suggested', decodeResult.suggested_response)}
                        title="Copy reply"
                        data-testid="decode-copy-suggested"
                      >
                        {copiedField === 'suggested' ? <Check className="h-3.5 w-3.5 text-[#00E5CC]" /> : <Copy className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>
                )}

                <p className="text-[11px] text-muted-foreground italic text-center pt-1">
                  {t('decoderDisclaimer') || "AI-assisted reading — context matters. Use as a starting point, not a verdict."}
                </p>
              </CardContent>
            </Card>
          )}

          {/* History */}
          {history.length > 1 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t('recentHistory')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {history.slice(1, 6).map((item, i) => (
                  <div
                    key={item.id || `${item.timestamp || ''}-${i}`}
                    className="p-2 rounded border text-xs cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => {
                      setInputText(item.original);
                      setResult(item);
                    }}
                  >
                    <p className="text-muted-foreground truncate">{item.original}</p>
                    <p className="font-medium truncate mt-0.5">{item.rephrased}</p>
                    <span className="text-[10px] text-muted-foreground capitalize">{item.tone}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <BottomNav />
    </div>
  );
};

export default ThoughtInterpreterPage;
